import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { useParams } from 'react-router';
import QRCode from 'qrcode';
import {
  ASSET_KINDS,
  PACKAGE_REQUESTS,
  PROSPECT_TEXTS,
  ROI_CALCULATORS,
  ROI_LABELS,
  WR_COMPANY,
  computeRoi,
  fileSize,
  providerName,
  roiDisplay,
  vCard,
  type AssetKind,
  type PackageRequest,
  type RoiCalculatorId,
  type RoiInputs,
  type VideoProvider,
} from '@wr/shared';
import { ApiError, get, post } from '../../api.js';
import { PdfOverlay, VideoOverlay } from '../content/Viewers.js';

type State = 'loading' | 'gate' | 'open' | 'expired' | 'missing' | 'error';

interface PageItem {
  id: string;
  format: 'video' | 'pdf';
  kind: string;
  title: string;
  description: string | null;
  provider: VideoProvider | null;
  video_id: string | null;
  video_hash: string | null;
  size_bytes: number | null;
  has_thumbnail: boolean;
}

interface Page {
  preview: boolean;
  title: string;
  message: string | null;
  lang: 'en' | 'fr';
  company: string;
  recipient: string;
  expiresOn: string | null;
  items: PageItem[];
  roi: { calculator: RoiCalculatorId; inputs: RoiInputs } | null;
  seller: {
    firstName: string;
    lastName: string;
    title: string | null;
    email: string;
    mobile: string | null;
    office: string | null;
    linkedin: string | null;
  };
  privacyUrl: string | null;
}

const longDate = (iso: string, lang: 'en' | 'fr') =>
  new Date(`${iso.slice(0, 10)}T12:00:00Z`).toLocaleDateString(lang === 'fr' ? 'fr-CA' : 'en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });

/**
 * The prospect page (brief section 7.6, docs/prototype.html), at
 * /p/<token>, without sign-in: the email gate, then the title, message,
 * ROI, videos, documents, next steps, the seller's card with its QR code
 * and the privacy notice, in the package's language. A seller who may
 * read the package sees it as a preview, recording nothing.
 */
export function ProspectPage() {
  const { token = '' } = useParams();
  const base = `/p/${encodeURIComponent(token)}`;
  const [state, setState] = useState<State>('loading');
  const [lang, setLang] = useState<'en' | 'fr'>('en');
  const [company, setCompany] = useState('');
  const [page, setPage] = useState<Page | null>(null);

  const [version, setVersion] = useState(0);
  const reload = useCallback(() => setVersion((v) => v + 1), []);

  useEffect(() => {
    let current = true;
    void (async () => {
      try {
        const head = await get<{ state: State; lang: 'en' | 'fr'; company?: string }>(base);
        if (!current) return;
        setLang(head.lang);
        setCompany(head.company ?? '');
        if (head.state !== 'open') {
          setState(head.state);
          return;
        }
        const full = await get<Page>(`${base}/page`);
        if (!current) return;
        setPage(full);
        setState('open');
      } catch (err) {
        if (!current) return;
        if (err instanceof ApiError && err.status === 404) setState('missing');
        else if (err instanceof ApiError && err.status === 410) setState('expired');
        else if (err instanceof ApiError && err.status === 401) setState('gate');
        else setState('error');
      }
    })();
    return () => {
      current = false;
    };
  }, [base, version]);

  const t = PROSPECT_TEXTS[lang];
  let body;
  if (state === 'loading') body = <div className="spinner" aria-label="Loading" />;
  else if (state === 'missing' || state === 'error') {
    body = (
      <div className="p-hero">
        <h1>{t.missing}</h1>
      </div>
    );
  } else if (state === 'expired') {
    body = (
      <div className="p-hero">
        <h1>{t.expired}</h1>
        <p>{t.expiredHint}</p>
      </div>
    );
  } else if (state === 'gate') {
    body = <Gate base={base} lang={lang} company={company} onOpen={reload} />;
  } else if (page) {
    body = <Content base={base} page={page} />;
  }

  return (
    <div className="prospect" role="main" lang={lang}>
      {page?.preview && (
        <div className="pv-bar">
          <span>Prospect page preview</span>
          <button type="button" className="btn sm" onClick={() => window.close()}>
            Close
          </button>
        </div>
      )}
      <div className="p-top">
        <img src="/logo.png" alt={WR_COMPANY.name} width={140} height={27} />
        <span>{WR_COMPANY.tagline}</span>
      </div>
      {body}
      <div className="p-foot">
        <span>
          <b>{WR_COMPANY.name}</b>, {WR_COMPANY.address}
        </span>
        <span>
          {WR_COMPANY.email}, {WR_COMPANY.phone}
        </span>
      </div>
    </div>
  );
}

function Gate({
  base,
  lang,
  company,
  onOpen,
}: {
  base: string;
  lang: 'en' | 'fr';
  company: string;
  onOpen(): void;
}) {
  const t = PROSPECT_TEXTS[lang];
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setError(t.badEmail);
      return;
    }
    setBusy(true);
    setError('');
    try {
      await post(`${base}/open`, { email: email.trim() });
      onOpen();
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 403
          ? t.refused
          : err instanceof ApiError && err.status === 429
            ? 'Too many tries. Wait a few minutes.'
            : 'No connection. Try again.',
      );
      setBusy(false);
    }
  }

  return (
    <form className="p-gate" noValidate onSubmit={(e) => void submit(e)}>
      <span className="lockic" aria-hidden="true">
        🔒
      </span>
      <h1>{t.privatePage}</h1>
      <p>
        {t.preparedFor} {company}.
      </p>
      <label className="f">
        {t.workEmail}
        <input
          type="email"
          inputMode="email"
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </label>
      <button type="submit" className="btn primary huge" disabled={busy}>
        {t.open}
      </button>
      <p className="err" role="alert">
        {error}
      </p>
      <p className="note">{t.notIndexed}</p>
    </form>
  );
}

function Content({ base, page }: { base: string; page: Page }) {
  const t = PROSPECT_TEXTS[page.lang];
  const [video, setVideo] = useState<PageItem | null>(null);
  const [pdf, setPdf] = useState<PageItem | null>(null);
  const videos = page.items.filter((i) => i.format === 'video');
  const documents = page.items.filter((i) => i.format === 'pdf');
  const api = `/api${base}`;
  const download = (path: string) => {
    window.location.href = `${api}${path}`;
  };

  return (
    <>
      <div className="p-hero">
        <h1>{page.title}</h1>
        <p>
          {t.preparedFor} {[page.company, page.recipient].filter(Boolean).join(', ')}.
          {page.expiresOn ? ` ${t.availableUntil} ${longDate(page.expiresOn, page.lang)}.` : ''}
        </p>
      </div>
      <div className="p-wrap">
        {page.message && <div className="p-msg">{page.message}</div>}
        {page.roi && <Roi page={page} api={api} />}
        {videos.length > 0 && (
          <>
            <h2 className="p-h2">{t.videos}</h2>
            <div className="p-list">
              {videos.map((v) => (
                <div key={v.id} className="p-item">
                  <span className="mini">
                    <Thumb api={api} item={v} />
                  </span>
                  <div>
                    <h3>{v.title}</h3>
                    <p className="meta">{providerName(v.provider)}</p>
                    {v.description && <p className="p-desc">{v.description}</p>}
                  </div>
                  <div className="actions">
                    <button
                      type="button"
                      className="btn primary"
                      onClick={() => {
                        setVideo(v);
                        if (!page.preview)
                          void post(`${base}/watched`, { assetId: v.id }).catch(() => undefined);
                      }}
                    >
                      {t.watch}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
        {documents.length > 0 && (
          <>
            <h2 className="p-h2">{t.documents}</h2>
            <div className="p-list">
              {documents.map((d) => (
                <div key={d.id} className="p-item">
                  <span className="mini">
                    <Thumb api={api} item={d} />
                  </span>
                  <div>
                    <h3>{d.title}</h3>
                    <p className="meta">
                      {[ASSET_KINDS[d.kind as AssetKind] ?? 'PDF', fileSize(d.size_bytes)]
                        .filter(Boolean)
                        .join(', ')}
                    </p>
                    {d.description && <p className="p-desc">{d.description}</p>}
                  </div>
                  <div className="actions">
                    <button type="button" className="btn primary" onClick={() => setPdf(d)}>
                      {t.view}
                    </button>
                    <button
                      type="button"
                      className="btn"
                      onClick={() => download(`/assets/${d.id}/file?download=1`)}
                    >
                      {t.download}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
        <NextSteps base={base} lang={page.lang} preview={page.preview} />
        <SellerCard api={api} page={page} />
        <p className="note p-privacy">
          {t.privacy}{' '}
          {page.privacyUrl && (
            <a href={page.privacyUrl} target="_blank" rel="noreferrer">
              {t.privacyLink}
            </a>
          )}
        </p>
      </div>
      {video?.provider && video.video_id && (
        <VideoOverlay
          title={video.title}
          closeLabel={t.close}
          video={{ provider: video.provider, id: video.video_id, hash: video.video_hash }}
          onClose={() => setVideo(null)}
        />
      )}
      {pdf && (
        <PdfOverlay
          title={pdf.title}
          url={`${api}/assets/${pdf.id}/file`}
          closeLabel={t.close}
          texts={{ opening: '…', failed: t.missing, download: t.download }}
          onClose={() => setPdf(null)}
          onDownload={() => download(`/assets/${pdf.id}/file?download=1`)}
        />
      )}
    </>
  );
}

function Thumb({ api, item }: { api: string; item: PageItem }) {
  const src = `${api}/assets/${item.id}/thumbnail`;
  if (item.format === 'video') {
    return item.has_thumbnail ? (
      <span className="thumb-wrap">
        <img className="thumb-img" src={src} alt="" loading="lazy" />
        <span className="playdot" />
      </span>
    ) : (
      <span className="vtile">
        <span className="prov">{providerName(item.provider).toUpperCase()}</span>
        <span className="vt">{item.title}</span>
        <span className="playdot" />
      </span>
    );
  }
  return item.has_thumbnail ? (
    <img className="thumb-img top" src={src} alt="" loading="lazy" />
  ) : (
    <span className={`pdftile k-${item.kind}`}>
      <b>PDF</b>
      <span>{item.title}</span>
    </span>
  );
}

/** The ROI's key figures, computed by the portal's engine, in the calculator's language. */
function Roi({ page, api }: { page: Page; api: string }) {
  const t = PROSPECT_TEXTS[page.lang];
  const roi = page.roi!;
  const display = roiDisplay(computeRoi(roi.inputs), roi.calculator);
  const labels = ROI_LABELS[ROI_CALCULATORS[roi.calculator].lang];
  return (
    <>
      <h2 className="p-h2">{t.roi}</h2>
      <div className="p-roi">
        <div className="kpis">
          <div className="kpi main">
            <b>{display.text.payback}</b>
            <span>{labels.keyFigures.payback}</span>
          </div>
          <div className="kpi">
            <b>{display.text.roiYear1}</b>
            <span>{labels.keyFigures.roiYear1}</span>
          </div>
          <div className="kpi">
            <b>{display.text.roiOverYears}</b>
            <span>{display.roiOverYearsLabel}</span>
          </div>
          <div className="kpi">
            <b>{display.text.robotProfit}</b>
            <span>{labels.keyFigures.profit}</span>
          </div>
        </div>
        <div className="p-roi-dl">
          <a className="btn" href={`${api}/roi.pdf`}>
            {t.downloadRoi}
          </a>
        </div>
      </div>
    </>
  );
}

/** Next steps (book a call, a question, a budget price, a reference visit): each a HubSpot task. */
function NextSteps({ base, lang, preview }: { base: string; lang: 'en' | 'fr'; preview: boolean }) {
  const t = PROSPECT_TEXTS[lang];
  const [open, setOpen] = useState<PackageRequest | null>(null);
  const [message, setMessage] = useState('');
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  async function send(e: FormEvent) {
    e.preventDefault();
    if (!open) return;
    setBusy(true);
    try {
      if (!preview)
        await post(`${base}/requests`, { kind: open, message: message.trim() || undefined });
      setSent(true);
      setMessage('');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <h2 className="p-h2">{t.nextSteps}</h2>
      <div className="pact">
        {PACKAGE_REQUESTS.map((kind) => (
          <button
            key={kind}
            type="button"
            className="pact-b"
            aria-pressed={open === kind}
            onClick={() => {
              setOpen(kind);
              setSent(false);
            }}
          >
            <b>{t.requests[kind][0]}</b>
            <span>{t.requests[kind][1]}</span>
          </button>
        ))}
      </div>
      {open && (
        <form className={`pact-form${sent ? ' ok' : ''}`} onSubmit={(e) => void send(e)}>
          {sent ? (
            <p role="status">{t.sent}</p>
          ) : (
            <>
              <b>{t.requests[open][0]}</b>
              <textarea
                aria-label={t.requestPlaceholder}
                placeholder={t.requestPlaceholder}
                maxLength={2000}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
              />
              <button type="submit" className="btn primary" disabled={busy}>
                {t.send}
              </button>
            </>
          )}
        </form>
      )}
    </>
  );
}

/** The seller's card with its QR code (a vCard) and Save contact. */
function SellerCard({ api, page }: { api: string; page: Page }) {
  const t = PROSPECT_TEXTS[page.lang];
  const s = page.seller;
  const [qr, setQr] = useState('');
  useEffect(() => {
    let current = true;
    void QRCode.toString(
      vCard({
        firstName: s.firstName,
        lastName: s.lastName,
        title: s.title,
        email: s.email,
        mobile: s.mobile,
        office: s.office,
        linkedin: s.linkedin,
      }),
      { type: 'svg', margin: 0, errorCorrectionLevel: 'M' },
    ).then((svg) => current && setQr(svg));
    return () => {
      current = false;
    };
  }, [s]);
  const phones = [s.mobile, s.office].filter(Boolean).join(', ');
  return (
    <div className="p-rep">
      <div
        className="p-rep-qr"
        role="img"
        aria-label={`QR code: ${s.firstName} ${s.lastName}`}
        dangerouslySetInnerHTML={{ __html: qr }}
      />
      <div>
        <span className="note">{t.yourContact}</span>
        <b>
          {s.firstName} {s.lastName}
        </b>
        {s.title && <span>{s.title}</span>}
        <span>{[phones, s.email].filter(Boolean).join(', ')}</span>
        <a className="btn sm p-save" href={`${api}/card.vcf`}>
          {t.saveContact}
        </a>
      </div>
    </div>
  );
}
